/**
 * Утилиты для преобразования данных между форматами фронтенда и API
 * Обеспечивает совместимость между компонентами React и Jataka Calculator API
 */

import { devWarn, devError } from './devLog';
import { 
  JatakaRequest, 
  JatakaResponse, 
  BasicInfo,
  GrahaPosition as ApiGrahaPosition
} from '../models/api';
import { 
  ParsedChartData, 
  GrahaPosition as FrontendGrahaPosition, 
  GrahaName, 
  RasiName 
} from '../models/types';
import { ChartDateTime } from '../models/storage';

// ===== BASIC INFO HELPERS (DRY) =====
// IMPORTANT: BasicInfo has two location-related fields that are often confused:
// - location: Coordinates in DMS format (e.g., "48°35'N 34°33'E")
// - place_name: Place name string (e.g., "Kyiv, Ukraine")

/**
 * Get place name from BasicInfo
 * Use this when you need the human-readable location name
 *
 * @param basicInfo - API BasicInfo object
 * @param fallback - Fallback value if place_name is not available
 * @returns Place name string (e.g., "Kyiv, Ukraine")
 */
export function getPlaceName(basicInfo: BasicInfo | undefined | null, fallback: string = 'Unknown'): string {
  return basicInfo?.place_name || fallback;
}

/**
 * Get coordinates string from BasicInfo (DMS format)
 * Use this when you need the coordinates display
 *
 * @param basicInfo - API BasicInfo object
 * @param fallback - Fallback value if location is not available
 * @returns Coordinates in DMS format (e.g., "48°35'N 34°33'E")
 */
export function getCoordinatesString(basicInfo: BasicInfo | undefined | null, fallback: string = ''): string {
  return basicInfo?.location || fallback;
}

// ===== TYPES FOR BIRTH DATA =====

/**
 * Данные о рождении из формы NewChartModal
 */
export interface BirthData {
  chartName: string;
  locationName: string;
  date: string;           // DD-MM-YYYY format
  time: string;           // HH:MM:SS format
  longitude: number;      // Decimal degrees
  latitude: number;       // Decimal degrees
  timezoneMode: 'auto' | 'manual';
  timezoneOffset: string; // +HH:MM format
  selectedLocation: string;
}

// ===== BIRTH DATA TO API REQUEST =====

/**
 * Преобразование данных формы в запрос API
 * 
 * @param birthData - Данные из формы NewChartModal
 * @returns JatakaRequest готовый для отправки в API
 */
export function birthDataToJatakaRequest(birthData: BirthData): JatakaRequest {
  // Преобразование даты из DD-MM-YYYY в YYYY-MM-DD
  const [day, month, year] = birthData.date.split('-');
  const isoDate = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
  
  // Создание ISO 8601 datetime строки
  const datetime = `${isoDate}T${birthData.time}`;
  
  // Преобразование timezone offset из "+HH:MM" в число часов
  const timezoneOffset = parseTimezoneOffset(birthData.timezoneOffset);
  
  // TEMPORARY FIX: Backend expects integer for timezone_offset but many timezones have fractional hours
  // Round to nearest integer until backend is fixed to accept floats
  const roundedTimezoneOffset = Math.round(timezoneOffset);
  
  return {
    datetime,
    latitude: birthData.latitude,
    longitude: birthData.longitude,
    timezone_offset: roundedTimezoneOffset, // Rounded to integer for backend compatibility
    place_name: birthData.locationName || birthData.selectedLocation || birthData.chartName,
    altitude: 0 // Default altitude
  };
}

/**
 * Парсинг строки часового пояса в число часов от UTC
 * 
 * @param timezoneStr - Строка вида "+03:00" или "-05:30"
 * @returns Число часов от UTC (например, 3 или -5.5)
 */
export function parseTimezoneOffset(timezoneStr: string): number {
  // Удаляем пробелы и проверяем формат
  const cleaned = timezoneStr.trim();
  const match = cleaned.match(/^([+-])(\d{1,2}):(\d{2})$/);
  
  if (!match) {
    // // devWarn(`Invalid timezone format: ${timezoneStr}, defaulting to 0`);
    return 0;
  }
  
  const [, sign, hours, minutes] = match;
  const hourValue = parseInt(hours, 10);
  const minuteValue = parseInt(minutes, 10);
  
  const totalHours = hourValue + (minuteValue / 60);
  return sign === '+' ? totalHours : -totalHours;
}

/**
 * Форматирование числа часов в строку часового пояса
 * 
 * @param offsetHours - Часы от UTC (например, 3 или -5.5)
 * @returns Строка вида "+03:00" или "-05:30"
 */
export function formatTimezoneOffset(offsetHours: number): string {
  const sign = offsetHours >= 0 ? '+' : '-';
  const absHours = Math.abs(offsetHours);
  const hours = Math.floor(absHours);
  const minutes = Math.round((absHours - hours) * 60);
  
  return `${sign}${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
}

// ===== API RESPONSE TO CHART DATA =====

/**
 * Преобразование ответа API в данные чарта для фронтенда
 * 
 * @param apiResponse - Ответ от Jataka Calculator API
 * @param originalBirthData - Исходные данные о рождении
 * @returns ParsedChartData для использования в компонентах чарта
 */
export function jatakaResponseToChartData(
  apiResponse: JatakaResponse,
  originalBirthData: BirthData
): ParsedChartData {
  // Преобразуем ВСЕ планеты включая Лагну в единый массив (новая архитектура)
  const planets: FrontendGrahaPosition[] = convertApiPlanetsToFrontend(
    apiResponse.graha_positions.planets // Включаем все планеты + Лагну
  );

  // Создаем ChartDateTime из API ответа
  const chartDateTime: ChartDateTime = createChartDateTimeFromApi(
    apiResponse.basic_info,
    originalBirthData
  );

  // Создаем описание чарта с информацией от API
  const chartDescription = createChartDescription(apiResponse, originalBirthData);

  // Создаем объект с информацией о хоре для EventManager
  // Check for detailed_panchanga.hora_details.ruling_planet
  let horaGrahaName: GrahaName | null = null;

  if (apiResponse.detailed_panchanga?.hora_details?.current_planet) {
    // Direct mapping from detailed hora data using current_planet field
    horaGrahaName = mapPlanetNameToGrahaName(apiResponse.detailed_panchanga.hora_details.current_planet);
  } else {
    horaGrahaName = null;
  }

  const horaInfo = {
    graha: horaGrahaName,
    lord: apiResponse.panchanga.hora_lord,
    currentPlanet: apiResponse.detailed_panchanga?.hora_details?.current_planet // Add API field
  };

  // Extract muhurta grahas for chakra rendering
  const muhurtaGrahas = extractMuhurtaGrahas(apiResponse);

  // Extract center coordinates for muhurta sectors
  const centerDMS = extractCenterCoordinates(apiResponse);

  // Debug logging для хоры

  return {
    planets,
    hora: horaInfo,
    muhurtaGrahas,
    centerDMS,
    parseErrors: [],
    originalData: chartDescription,
    chartDateTime,
    sunriseSuryaLongitude: apiResponse.astronomical?.sunrise_surya_longitude, // ✅ Add sunrise longitude from API

    // Detailed panchanga data directly on ParsedChartData for easy access
    detailed_panchanga: apiResponse.detailed_panchanga,

    // Сохраняем полный API ответ в формате JatakaApiResponse
    apiResponse: {
      basic_info: {
        ...apiResponse.basic_info,
        chart_name: originalBirthData.chartName,
        // Preserve original numeric coordinates for animation system - use standard field names
        latitudeValue: originalBirthData.latitude,
        longitudeValue: originalBirthData.longitude
      },
      graha_positions: apiResponse.graha_positions as any,  // Cast to ApiGrahaPositions format
      astronomical: apiResponse.astronomical as any,      // Cast to ApiAstronomical format
      panchanga: apiResponse.panchanga as any,            // Cast to ApiPanchanga format
      detailed_panchanga: apiResponse.detailed_panchanga as any, // Cast to ApiDetailedPanchanga format
      calculation_metadata: {
        transformedAt: new Date().toISOString(),
        apiVersion: 'jataka-calculator-v1',
        originalCoordinates: {
          latitude: originalBirthData.latitude,
          longitude: originalBirthData.longitude
        }
      }
    }
  };
}

/**
 * Преобразование планетарных позиций от API в формат фронтенда
 *
 * @param apiPlanets - Планетарные позиции от API
 * @returns Массив планетарных позиций для фронтенда
 */
function convertApiPlanetsToFrontend(
  apiPlanets: ApiGrahaPosition[]
): FrontendGrahaPosition[] {
  return apiPlanets.map(planet => convertApiPlanetToFrontend(planet)).filter(Boolean) as FrontendGrahaPosition[];
}

/**
 * Преобразование одной планетарной позиции от API в формат фронтенда
 *
 * @param apiPlanet - Планетарная позиция от API
 * @returns Планетарная позиция для фронтенда
 */
function convertApiPlanetToFrontend(apiPlanet: ApiGrahaPosition): FrontendGrahaPosition | null {
  try {
    // Маппинг коротких названий API в GrahaName фронтенда
    const grahaNameMapping: Record<string, GrahaName> = {
      'Sy': GrahaName.Sy,
      'Ch': GrahaName.Ch,
      'Ma': GrahaName.Ma,
      'Bu': GrahaName.Bu,
      'Gu': GrahaName.Gu,
      'Sk': GrahaName.Sk,
      'Sa': GrahaName.Sa,
      'Ra': GrahaName.Rahu,
      'Ke': GrahaName.Ketu,
      'Lg': GrahaName.Lg
    };

    // Маппинг названий раши API в RasiName фронтенда
    const rasiNameMapping: Record<string, RasiName> = {
      'Mesha': RasiName.Mesha,
      'Vrishabha': RasiName.Vrishabha,
      'Mithuna': RasiName.Mithuna,
      'Karka': RasiName.Karka,
      'Simha': RasiName.Simha,
      'Kanya': RasiName.Kanya,
      'Tula': RasiName.Tula,
      'Vrishchika': RasiName.Vrishchika,
      'Dhanu': RasiName.Dhanu,
      'Makara': RasiName.Makara,
      'Kumbha': RasiName.Kumbha,
      'Meena': RasiName.Meena
    };

    const grahaName = grahaNameMapping[apiPlanet.short_name];
    const rasiName = rasiNameMapping[apiPlanet.rasi_name];

    if (!grahaName || !rasiName) {
      // // devWarn(`Unknown graha or rasi: ${apiPlanet.short_name}, ${apiPlanet.rasi_name}`);
      return null;
    }

    // Получаем унифицированную позицию из position поля
    const positionString = apiPlanet.position;
    if (!positionString) {
      devWarn(`No position field for planet ${apiPlanet.short_name}`);
      return null;
    }
    const { longitude } = extractLongitudeAndSpeed(positionString);
    
    // Validate longitude before conversion
    if (typeof longitude !== 'number' || isNaN(longitude)) {
      devWarn(`Invalid longitude for planet ${apiPlanet.short_name}: ${longitude}`);
      return null;
    }

    // Debug logging for planet positioning
    if (apiPlanet.short_name === 'Sk' || apiPlanet.short_name === 'Ch') {
      // const containerId = convertRasiNumberToContainerId(apiPlanet.rasi_number);
    }

    const position = convertPositionToCoordinate(positionString);

    // Hora detection is now handled in ElementPreparationEngine using hora_details.current_planet
    // No longer using legacy horaLord comparison

    const planetResult: FrontendGrahaPosition = {
      graha: grahaName,
      position,
      positionString, // УНИФИЦИРОВАННАЯ ПОЗИЦИЯ для анимации
      rasi: rasiName,
      nakshatra: undefined, // Пока не используем накшатры в фронтенде
      isRetrograde: apiPlanet.is_retrograde,
      metadata: {
        api_data: {
          name: apiPlanet.name,
          short_name: apiPlanet.short_name,
          longitude_dms: apiPlanet.longitude_dms,
          latitude: apiPlanet.latitude,
          rasi_number: apiPlanet.rasi_number, // API 1-based (1-12)
          container_id: convertRasiNumberToContainerId(apiPlanet.rasi_number), // North Indian layout mapping
          nakshatra_name: apiPlanet.nakshatra_name,
          nakshatra_pada: apiPlanet.nakshatra_pada,
          position: positionString // Унифицированная позиция в формате "rasi|degrees|longitude|speed"
        }
      }
    };

    // isHora field removed - hora detection now handled by API hora_details.current_planet

    return planetResult;

  } catch (error) {
    devError(`Error converting planet ${apiPlanet.name}:`, error);
    return null;
  }
}

/**
 * Преобразование планеты с Лагной в формат фронтенда
 * 
 * @param apiPlanet - Планета от API (Лагна)
 * @returns Лагна для фронтенда
 */


/**
 * Конвертация API rasi_number в container ID для North Indian layout
 * 
 * @param rasiNumber - API rasi_number (1-12)
 * @returns Container ID (0-11) для North Indian chart layout
 */
function convertRasiNumberToContainerId(rasiNumber: number): number {
  // North Indian layout mapping: [12][1][2][3] / [11][4] / [10][5] / [9][8][7][6]
  const mapping: Record<number, number> = {
    1: 1,   // Mesha -> Container-1
    2: 2,   // Vrishabha -> Container-2
    3: 3,   // Mithuna -> Container-3
    4: 4,   // Karka -> Container-4
    5: 5,   // Simha -> Container-5
    6: 6,   // Kanya -> Container-6
    7: 7,   // Tula -> Container-7
    8: 8,   // Vrishchika -> Container-8
    9: 9,   // Dhanu -> Container-9
    10: 10, // Makara -> Container-10
    11: 11, // Kumbha -> Container-11
    12: 0   // Meena -> Container-0
  };
  
  return mapping[rasiNumber] ?? 0;
}

/**
 * Извлечение longitude и speed из унифицированного поля position
 * Формат: "rasi|degrees|longitude|speed"
 */
function extractLongitudeAndSpeed(position: string): { longitude: number; speed: number } {
  const parts = position.split('|');
  if (parts.length >= 4) {
    return {
      longitude: parseFloat(parts[2]) || 0,
      speed: parseFloat(parts[3]) || 0
    };
  }
  return { longitude: 0, speed: 0 };
}

// Legacy function createPositionStringFromLegacyFields removed - no longer needed
// API now always provides position field

/**
 * Преобразование поля position API в координаты
 * Использует унифицированный формат "rasi|degrees|longitude|speed"
 * 
 * @param positionString - Строка position от API в формате "rasi|degrees|longitude|speed"
 * @returns Координата в формате градусы, минуты, секунды
 */
function convertPositionToCoordinate(positionString: string): { degrees: number; minutes: number; seconds: number } {
  if (!positionString || typeof positionString !== 'string') {
    throw new Error('Position string is required and must be a string');
  }

  const parts = positionString.split('|');
  if (parts.length !== 4) {
    throw new Error(`Invalid position format: expected "rasi|degrees|longitude|speed", got "${positionString}"`);
  }
  const degrees = parseFloat(parts[1]);      // degrees in rasi (0-30)

  if (isNaN(degrees) || degrees < 0 || degrees > 30) {
    throw new Error(`Invalid degrees in rasi: ${degrees}, must be between 0-30`);
  }

  const degreesInt = Math.floor(degrees);
  const minutesFloat = (degrees - degreesInt) * 60;
  const minutesInt = Math.floor(minutesFloat);
  const seconds = (minutesFloat - minutesInt) * 60;
  
  return { 
    degrees: degreesInt, 
    minutes: minutesInt, 
    seconds: seconds 
  };
}

/**
 * Extract muhurta grahas from API response for chakra rendering
 */
function extractMuhurtaGrahas(apiResponse: JatakaResponse): string[] {
  try {
    // Try to get from detailed_panchanga.muhurta_details
    if (apiResponse.detailed_panchanga?.muhurta_details?.graha_ruler) {
      // For now, create 30 default muhurta grahas with the current ruling graha
      const currentGraha = apiResponse.detailed_panchanga.muhurta_details.graha_ruler;
      return Array.from({ length: 30 }, (_, i) => `${currentGraha}${i + 1}`);
    }

    // Fallback: create default muhurta grahas
    return Array.from({ length: 30 }, (_, i) => `M${i + 1}`);
  } catch (error) {
    devWarn('⚠️ Failed to extract muhurta grahas from API response:', error);
    return Array.from({ length: 30 }, (_, i) => `M${i + 1}`);
  }
}

/**
 * Extract center coordinates for muhurta sectors from API response
 */
function extractCenterCoordinates(apiResponse: JatakaResponse): { degrees: number; minutes: number; seconds: number } | undefined {
  try {
    // Try to get from astronomical data or basic info
    if (apiResponse.basic_info?.latitudeValue && apiResponse.basic_info?.longitudeValue) {
      // For now, use latitude as the center reference
      // This is a simplified implementation - in reality, this should be calculated differently
      const lat = apiResponse.basic_info.latitudeValue;
      return {
        degrees: Math.floor(Math.abs(lat)),
        minutes: Math.floor((Math.abs(lat) % 1) * 60),
        seconds: Math.floor(((Math.abs(lat) % 1) * 60 % 1) * 60)
      };
    }

    return undefined;
  } catch (error) {
    devWarn('⚠️ Failed to extract center coordinates from API response:', error);
    return undefined;
  }
}


/**
 * Создание ChartDateTime из API ответа
 */
function createChartDateTimeFromApi(
  basicInfo: BasicInfo,
  originalBirthData: BirthData
): ChartDateTime {
  // Check if data is hidden (placeholder "-")
  if (!originalBirthData.date || originalBirthData.date === '-') {
    // Return placeholder data for hidden fields
    return {
      date: '-',
      time: originalBirthData.time === '-' ? '-' : originalBirthData.time,
      timezone: typeof originalBirthData.timezoneOffset === 'number'
        ? `+${String(originalBirthData.timezoneOffset).padStart(2, '0')}:00`
        : (originalBirthData.timezoneOffset || '+00:00'),
      location: originalBirthData.locationName === '-' ? '-' : (originalBirthData.locationName || basicInfo.place_name || originalBirthData.selectedLocation),
      originalDateStr: '-',
      originalTimeStr: originalBirthData.time === '-' ? '-' : originalBirthData.time
    };
  }

  let isoDate: string;

  if (originalBirthData.date.includes('-') && originalBirthData.date !== '-') {
    // Формат "DD-MM-YYYY"
    const [day, month, year] = originalBirthData.date.split('-');
    // Check if it's a valid date (not just a placeholder)
    if (day && month && year && year.length === 4) {
      isoDate = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    } else {
      // Invalid format, use placeholder
      isoDate = '-';
    }
  } else {
    // Формат "Month DD, YYYY" -> парсим через Date
    const parsedDate = new Date(originalBirthData.date);
    if (isNaN(parsedDate.getTime())) {
      // Invalid date, use placeholder
      isoDate = '-';
    } else {
      const year = parsedDate.getFullYear();
      const month = (parsedDate.getMonth() + 1).toString().padStart(2, '0');
      const day = parsedDate.getDate().toString().padStart(2, '0');
      isoDate = `${year}-${month}-${day}`;
    }
  }

  return {
    date: isoDate,
    time: originalBirthData.time,
    timezone: originalBirthData.timezoneOffset,
    location: originalBirthData.locationName || basicInfo.place_name || originalBirthData.selectedLocation,
    originalDateStr: originalBirthData.date,
    originalTimeStr: originalBirthData.time
  };
}

/**
 * Создание описания чарта с информацией от API
 */
function createChartDescription(
  apiResponse: JatakaResponse,
  originalBirthData: BirthData
): string {
  const { basic_info, panchanga, astronomical, graha_positions } = apiResponse;
  
  // Находим Лагну среди планет
  const lagnaPosition = graha_positions.planets.find(planet => planet.short_name === 'Lg');
  
  // Создаем секцию планетарных позиций (исключаем Лагну)
  const planetsSection = graha_positions.planets
    .filter(planet => planet.short_name !== 'Lg')
    .map(planet => {
      const { longitude } = extractLongitudeAndSpeed(planet.position || '0|0|0|0');
      return `${planet.name} (${planet.short_name}): ${longitude.toFixed(4)}° in ${planet.rasi_name}${planet.is_retrograde ? ' (R)' : ''}`;
    }).join('\n');

  const lagnaInfo = lagnaPosition 
    ? (() => {
        const { longitude } = extractLongitudeAndSpeed(lagnaPosition.position || '0|0|0|0');
        return `${longitude.toFixed(4)}° in ${lagnaPosition.rasi_name}`;
      })()
    : 'Unknown';

  return `Jataka Chart: ${originalBirthData.chartName}

=== Basic Information ===
Date: ${basic_info.date}
Time: ${basic_info.time}
Location: ${basic_info.location}
Timezone: ${basic_info.timezone}
Altitude: ${basic_info.altitude}

=== Panchanga ===
Lunar Year-Month: ${panchanga.lunar_year_month}
Tithi: ${panchanga.tithi}
Weekday: ${panchanga.weekday}
Nakshatra: ${panchanga.nakshatra}
Yoga: ${panchanga.yoga}
Karana: ${panchanga.karana}
Hora Lord: ${panchanga.hora_lord}${panchanga.muhurta ? `\nMuhurta: ${panchanga.muhurta}` : ''}${panchanga.ghati ? `\nGhati: ${panchanga.ghati}` : ''}

=== Graha Positions ===
Lagna: ${lagnaInfo}
${planetsSection}

=== Astronomical Data ===
Sunrise: ${astronomical.sunrise}
Sunset: ${astronomical.sunset}
Janma Ghatis: ${astronomical.janma_ghatis}
Ayanamsa: ${astronomical.ayanamsa}
Sidereal Time: ${astronomical.sidereal_time}
Julian Day: ${astronomical.julian_day}

Generated by Jataka Calculator API with Swiss Ephemeris precision`;
}


// ===== VALIDATION UTILITIES =====

/**
 * Валидация данных о рождении перед отправкой в API
 * 
 * @param birthData - Данные для валидации
 * @returns Массив ошибок валидации (пустой если все ок)
 */
export function validateBirthData(birthData: BirthData): string[] {
  const errors: string[] = [];

  // Валидация имени чарта
  if (!birthData.chartName.trim()) {
    errors.push('Chart name is required');
  }

  // Валидация даты
  if (!birthData.date.match(/^\d{2}-\d{2}-\d{4}$/)) {
    errors.push('Date must be in DD-MM-YYYY format');
  } else {
    const [day, month, year] = birthData.date.split('-').map(Number);
    if (year < 1900 || year > 2100) {
      errors.push('Year must be between 1900 and 2100');
    }
    if (month < 1 || month > 12) {
      errors.push('Month must be between 1 and 12');
    }
    if (day < 1 || day > 31) {
      errors.push('Day must be between 1 and 31');
    }
  }

  // Валидация времени
  if (!birthData.time.match(/^\d{2}:\d{2}:\d{2}$/)) {
    errors.push('Time must be in HH:MM:SS format');
  } else {
    const [hours, minutes, seconds] = birthData.time.split(':').map(Number);
    if (hours < 0 || hours > 23) {
      errors.push('Hours must be between 0 and 23');
    }
    if (minutes < 0 || minutes > 59) {
      errors.push('Minutes must be between 0 and 59');
    }
    if (seconds < 0 || seconds > 59) {
      errors.push('Seconds must be between 0 and 59');
    }
  }

  // Валидация координат
  if (birthData.latitude < -90 || birthData.latitude > 90) {
    errors.push('Latitude must be between -90 and 90 degrees');
  }
  if (birthData.longitude < -180 || birthData.longitude > 180) {
    errors.push('Longitude must be between -180 and 180 degrees');
  }

  // Валидация часового пояса
  const timezoneOffset = parseTimezoneOffset(birthData.timezoneOffset);
  if (timezoneOffset < -12 || timezoneOffset > 12) {
    errors.push('Timezone offset must be between -12 and +12 hours');
  }

  return errors;
}

/**
 * Проверка валидности данных о рождении
 * 
 * @param birthData - Данные для проверки
 * @returns true если данные валидны
 */
export function isValidBirthData(birthData: BirthData): boolean {
  return validateBirthData(birthData).length === 0;
}

/**
 * Ensures that chartData has proper chart_name in basic_info
 * Works for all types of charts: new, imported, loaded from storage
 * 
 * @param chartData - Original chart data
 * @param chartName - Chart name from metadata or form
 * @returns Chart data with guaranteed chart_name in basic_info
 */
export function ensureChartNameInBasicInfo(chartData: ParsedChartData, chartName?: string): ParsedChartData {
  if (!chartData.apiResponse) {
    return chartData;
  }

  // Determine the chart name to use
  const nameToUse = chartName ||
                   chartData.apiResponse.basic_info?.chart_name ||
                   chartData.chartDateTime?.location ||
                   'Unnamed Chart';

  // Use basic_info if available
  const baseBasicInfo = chartData.apiResponse.basic_info || {};

  const result = {
    ...chartData,
    apiResponse: {
      ...chartData.apiResponse,
      basic_info: {
        ...baseBasicInfo,
        chart_name: nameToUse
      }
    }
  };

  return result;
}

/**
 * Маппинг названий планет из detailed_panchanga в короткие названия планет
 * 
 * @param planetName - Название планеты от API (Surya, Chandra, etc.)
 * @returns Короткое название планеты (GrahaName)
 */
function mapPlanetNameToGrahaName(planetName: string): GrahaName | null {
  const planetNameMapping: Record<string, GrahaName> = {
    'Surya': GrahaName.Sy,
    'Chandra': GrahaName.Ch,
    'Mangala': GrahaName.Ma,
    'Budha': GrahaName.Bu,
    'Guru': GrahaName.Gu,
    'Shukra': GrahaName.Sk,
    'Shani': GrahaName.Sa,
    'Rahu': GrahaName.Rahu,
    'Ketu': GrahaName.Ketu
  };
  
  return planetNameMapping[planetName] || null;
}


/**
 * Преобразование API graha данных в формат для отображения планет
 * Используется в компонентах Planet display sections
 * 
 * @param apiGrahaPositions - Массив позиций планет от API
 * @returns Массив планет в формате для planetFormatters
 */
export function transformApiGrahaForDisplay(apiGrahaPositions: any[]): any[] {
  return apiGrahaPositions.map(apiPlanet => {
    // Handle both API format (ApiGrahaPosition) and frontend format (already transformed)
    // If it's already transformed frontend data with metadata.api_data, extract the API data
    let sourceData = apiPlanet;
    if (apiPlanet.metadata?.api_data && !apiPlanet.position) {
      sourceData = apiPlanet.metadata.api_data;
    }
    
    // Маппинг коротких названий API
    const grahaNameMapping: Record<string, string> = {
      'Sy': 'Sy',
      'Ch': 'Ch', 
      'Ma': 'Ma',
      'Bu': 'Bu',
      'Gu': 'Gu',
      'Sk': 'Sk',
      'Sa': 'Sa',
      'Ra': 'Rahu',
      'Ke': 'Ketu',
      'Lg': 'Lg'
    };

    const graha = grahaNameMapping[apiPlanet.short_name] || apiPlanet.short_name;

    // Extract longitude and speed from position field
    const positionString = apiPlanet.position;
    if (!positionString) {
      devWarn(`No position field for planet ${apiPlanet.short_name}`);
      return null;
    }
    const { longitude, speed } = extractLongitudeAndSpeed(positionString);

    // Возвращаем данные в формате, который ожидает calculatePlanetData
    return {
      name: apiPlanet.name,
      short_name: apiPlanet.short_name,
      graha: graha,
      longitude: longitude,
      longitude_dms: apiPlanet.longitude_dms,
      latitude: apiPlanet.latitude,
      speed: speed,
      is_retrograde: apiPlanet.is_retrograde,
      rasi_number: apiPlanet.rasi_number,
      rasi_name: apiPlanet.rasi_name,
      nakshatra_number: apiPlanet.nakshatra_number,
      nakshatra_name: apiPlanet.nakshatra_name,
      nakshatra_pada: apiPlanet.nakshatra_pada,
      navamsa_rasi: apiPlanet.navamsa_rasi,
      position: positionString // Сохраняем поле position для отрисовки в чарте
    };
  });
}

// ===== DEBUGGING UTILITIES =====

/**
 * Логирование преобразования данных для отладки
 */
export function logDataTransformation(
  originalData: BirthData,
  apiRequest: JatakaRequest,
  apiResponse?: JatakaResponse,
  chartData?: ParsedChartData
): void {
  // console.group('🔄 Data Transformation Debug');
  // if (apiResponse) {
  // }
  // if (chartData) {
  // }
  // console.groupEnd();
}